import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import StructureSetItem from '~/components/Structures/StructureSetItem';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { useAvailableStructureSetsView } from '~/hooks/domain/useAvailableStructureSetsView';
import { useLiveStructureSet } from '~/hooks/domain/useLiveStructureSet';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';

export default function StructureSetList() {
    const intl = useIntl();

    const availableStructureSets = useAvailableStructureSetsView(true);
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const { pickStructureSet } = useConfirmedSelection();
    const { deleteStructureSet } = useDeleteDataset();

    const [deleteTarget, setDeleteTarget] =
        useState<AvailableStructureSetView | null>(null);
    // Resolve to live SS for the actual delete call. useLiveStructureSet
    // is a hook so it must be called at the top level; it returns null
    // when the dialog is closed or the SS isn't loaded.
    const deleteTargetLive = useLiveStructureSet(deleteTarget?.structureSetId);

    const handleConfirmDelete = () => {
        if (deleteTargetLive) {
            deleteStructureSet(deleteTargetLive);
        }
        setDeleteTarget(null);
    };

    if (availableStructureSets.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                <Box px={2} pt={1.5} pb={1}>
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="structureSets" />
                    </Typography>
                </Box>
                {availableStructureSets.map((ss) => {
                    const isActive =
                        currentStructureSetId === ss.sopInstanceUid;
                    return (
                        <Box
                            key={ss.sopInstanceUid}
                            borderTop="1px solid"
                            borderColor="divider"
                        >
                            <StructureSetItem
                                structureSet={ss}
                                isActive={isActive}
                                allowReselect
                                onClick={() => pickStructureSet(ss)}
                                onDelete={
                                    ss.structureSetId
                                        ? () => setDeleteTarget(ss)
                                        : undefined
                                }
                            />
                        </Box>
                    );
                })}
            </Box>

            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteStructureSet' })}
                message={intl.formatMessage({
                    id: 'deleteStructureSetConfirm',
                })}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />
        </>
    );
}
